import { useState, useCallback, useMemo } from 'react';
import { NoiseSuppressionProcessor as ShiguredoProcessor } from '@shiguredo/noise-suppression';
import { LocalAudioTrack, Track, type TrackProcessor, type ProcessorOptions } from 'livekit-client';

class RNNoiseProcessor implements TrackProcessor<Track.Kind.Audio> {
  name = 'rnnoise-shiguredo';
  private processor: ShiguredoProcessor;
  processedTrack?: MediaStreamTrack;

  constructor() {
    this.processor = new ShiguredoProcessor();
  }

  async init(opts: ProcessorOptions<Track.Kind.Audio>) {
    console.log('Initializing RNNoise processor');
    try {
      this.processedTrack = await this.processor.startProcessing(opts.track as MediaStreamTrack);
    } catch (e) {
      console.error('RNNoise startProcessing failed:', e);
      throw e;
    }
  }

  async restart(opts: ProcessorOptions<Track.Kind.Audio>) {
    console.log('Restarting RNNoise processor');
    this.processor.stopProcessing();
    await this.init(opts);
  }

  async destroy() {
    console.log('Destroying RNNoise processor');
    this.processor.stopProcessing();
  }
}

export interface UseNoiseSuppressionResult {
  isNoiseFilterEnabled: boolean;
  isNoiseFilterPending: boolean;
  setNoiseFilterEnabled: (enabled: boolean) => Promise<void>;
  isSupported: boolean;
}

export function useNoiseSuppression(
  microphoneTrack: LocalAudioTrack | undefined
): UseNoiseSuppressionResult {
  const [isNoiseFilterEnabled, setIsNoiseFilterEnabled] = useState(false);
  const [isNoiseFilterPending, setIsNoiseFilterPending] = useState(false);

  const processor = useMemo(() => new RNNoiseProcessor(), []);

  const setNoiseFilterEnabled = useCallback(
    async (enabled: boolean) => {
      const audioTrack = microphoneTrack;
      if (!audioTrack) {
        console.warn('No audio track available for noise suppression');
        return;
      }

      setIsNoiseFilterPending(true);
      try {
        if (enabled) {
          console.log('Enabling RNNoise suppression');
          await audioTrack.setProcessor(processor);
          setIsNoiseFilterEnabled(true);
        } else {
          console.log('Disabling RNNoise suppression');
          await audioTrack.stopProcessor();
          setIsNoiseFilterEnabled(false);
        }
      } catch (error) {
        console.error('Failed to toggle noise filter:', error);
        setIsNoiseFilterEnabled(false);
      } finally {
        setIsNoiseFilterPending(false);
      }
    },
    [microphoneTrack, processor]
  );

  return {
    isNoiseFilterEnabled,
    isNoiseFilterPending,
    setNoiseFilterEnabled,
    isSupported: ShiguredoProcessor.isSupported()
  };
}
